import { useSearchParams } from 'react-router';
import { useMemo, useState, useEffect, useContext, useCallback, createContext } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter, usePathname } from 'src/routes/hooks';

import { useListHandler } from './use-handler';
import {
  expandToFolder,
  findFolderByPath,
  flattenAllReports,
  folderPathToExpandedMap,
  collectReportsFromFolder,
  parseFolderPathFromParams,
  REPORT_FOLDER_QUERY_PARAM,
  extractFolderPathFromReportUri,
} from './report-tree-utils';

// ----------------------------------------------------------------------

const ReportsRepositoryContext = createContext(null);

// ----------------------------------------------------------------------

function getActiveReportUri(pathname) {
  if (pathname.startsWith('/reports/')) {
    const uri = pathname.replace(/^\/reports/, '') || null;
    const firstSegment = uri?.split('/').filter(Boolean)[0];

    if (firstSegment === 'list' || firstSegment === 'schedules' || firstSegment === 'envysion') {
      return null;
    }

    return uri;
  }

  if (pathname.startsWith('/dashboards/')) {
    return pathname.replace(/^\/dashboards/, '') || null;
  }

  return null;
}

// ----------------------------------------------------------------------

export function ReportsRepositoryProvider({ children }) {
  const router = useRouter();
  const pathname = usePathname();
  const activeReportUri = getActiveReportUri(pathname);
  const [searchParams, setSearchParams] = useSearchParams();
  const folderPath = parseFolderPathFromParams(searchParams);
  const [expandedFolders, setExpandedFolders] = useState(() => folderPathToExpandedMap(folderPath));

  const { reports, favorites, loading, toggleFavorite, deleteReport: deleteReportFromHandler } =
    useListHandler('');

  useEffect(() => {
    if (!folderPath) {
      return;
    }

    // Expand the selected path without collapsing other open folders.
    setExpandedFolders((prev) => expandToFolder(prev, folderPath));
  }, [folderPath]);

  const selectedFolder = useMemo(() => {
    if (!folderPath) {
      return null;
    }

    const folder = findFolderByPath(reports, folderPath);

    return {
      path: folderPath,
      name: folder?.name || folderPath.split('/').pop(),
    };
  }, [folderPath, reports]);

  const selectedFolderReports = useMemo(() => {
    if (!reports.length) {
      return [];
    }

    // No folder selected: show every available resource in the list.
    if (!folderPath) {
      return flattenAllReports(reports);
    }

    const folder = findFolderByPath(reports, folderPath);
    // Selected folder: include reports from the folder and all descendants.
    return collectReportsFromFolder(folder);
  }, [reports, folderPath]);

  const setFolderPath = useCallback(
    (path) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);

          if (path) {
            next.set(REPORT_FOLDER_QUERY_PARAM, path);
          } else {
            next.delete(REPORT_FOLDER_QUERY_PARAM);
          }

          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleFolderClick = useCallback(
    (folderPathValue) => {
      if (activeReportUri) {
        const params = new URLSearchParams();
        params.set(REPORT_FOLDER_QUERY_PARAM, folderPathValue);
        router.push(`${paths.reports.list}?${params.toString()}`);
        return;
      }

      setFolderPath(folderPathValue);
      setExpandedFolders((prev) => expandToFolder(prev, folderPathValue));
    },
    [activeReportUri, router, setFolderPath]
  );

  const deleteReport = useCallback(
    async (uri) => {
      const success = await deleteReportFromHandler(uri);

      if (success && activeReportUri === uri) {
        const params = new URLSearchParams();
        const reportFolderPath = extractFolderPathFromReportUri(uri);

        if (reportFolderPath) {
          params.set(REPORT_FOLDER_QUERY_PARAM, reportFolderPath);
        }

        const listPath = params.toString()
          ? `${paths.reports.list}?${params.toString()}`
          : paths.reports.list;

        router.push(listPath);
      }

      return success;
    },
    [activeReportUri, deleteReportFromHandler, router]
  );

  const navigateToReport = useCallback(
    (uri, resourceType = 'reportUnit') => {
      const reportFolderPath = extractFolderPathFromReportUri(uri);
      const nextParams = new URLSearchParams(searchParams);

      nextParams.set('type', resourceType);

      if (reportFolderPath) {
        nextParams.set(REPORT_FOLDER_QUERY_PARAM, reportFolderPath);
      }

      const basePath =
        resourceType === 'dashboard' ? paths.dashboards.view(uri) : paths.reports.view(uri);

      router.push(`${basePath}?${nextParams.toString()}`);
    },
    [router, searchParams]
  );

  // Keep folder query in sync when viewing a report without an explicit folder param.
  useEffect(() => {
    if (!activeReportUri || loading || !reports.length) {
      return;
    }

    const reportFolderPath = extractFolderPathFromReportUri(activeReportUri);
    if (!reportFolderPath || folderPath === reportFolderPath) {
      return;
    }

    setFolderPath(reportFolderPath);
  }, [activeReportUri, folderPath, loading, reports.length, setFolderPath]);

  const value = useMemo(
    () => ({
      reports,
      favorites,
      loading,
      toggleFavorite,
      deleteReport,
      selectedFolder,
      selectedFolderReports,
      expandedFolders,
      activeReportUri,
      handleFolderClick,
      navigateToReport,
    }),
    [
      reports,
      favorites,
      loading,
      toggleFavorite,
      deleteReport,
      selectedFolder,
      selectedFolderReports,
      expandedFolders,
      activeReportUri,
      handleFolderClick,
      navigateToReport,
    ]
  );

  return (
    <ReportsRepositoryContext.Provider value={value}>{children}</ReportsRepositoryContext.Provider>
  );
}

export function useReportsRepository() {
  const context = useContext(ReportsRepositoryContext);

  if (!context) {
    throw new Error('useReportsRepository must be used within ReportsRepositoryProvider');
  }

  return context;
}
